// @ts-nocheck
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface UserProfileData {
  id: string;
  first_name: string | null;
  last_name: string | null;
  username: string | null;
  avatar_url: string | null;
  bio: string | null;
  primary_sport_id: string | null;
  primary_position_id: string | null;
  grad_year: number | null;
  state: string | null;
  city: string | null;
}

export interface ProfileStats {
  postsCount: number;
  followersCount: number;
  followingCount: number;
}

export function useUserProfile(usernameOrId?: string) {
  const { user } = useAuth();
  
  // If no username provided, use current user
  const isCurrentUser = !usernameOrId || usernameOrId === 'me';
  const targetId = isCurrentUser ? user?.id : null;
  const targetUsername = isCurrentUser ? null : usernameOrId;

  const profileQuery = useQuery({
    queryKey: ['user-profile', usernameOrId, user?.id],
    queryFn: async () => {
      let query = supabase
        .from('profiles')
        .select(`
          id,
          first_name,
          last_name,
          username,
          avatar_url,
          bio,
          primary_sport_id,
          primary_position_id,
          grad_year,
          state,
          city
        `);

      if (targetId) {
        query = query.eq('id', targetId);
      } else if (targetUsername) {
        query = query.eq('username', targetUsername);
      } else {
        throw new Error('No user identifier provided');
      }

      const { data, error } = await query.maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Profile not found');

      return data as UserProfileData;
    },
    enabled: !!(targetId || targetUsername),
  });

  return {
    profile: profileQuery.data,
    isLoading: profileQuery.isLoading,
    error: profileQuery.error,
    isCurrentUser: isCurrentUser && !!user?.id,
  };
}

export function useProfileStats(profileId: string | undefined) {
  return useQuery({
    queryKey: ['profile-stats', profileId],
    queryFn: async () => {
      if (!profileId) throw new Error('No profile ID');

      // Get posts count
      const { count: postsCount, error: postsError } = await supabase
        .from('wall_posts')
        .select('*', { count: 'exact', head: true })
        .eq('profile_id', profileId)
        .eq('is_hidden', false);

      if (postsError) throw postsError;

      // Get followers count
      const { count: followersCount, error: followersError } = await supabase
        .from('profile_follows')
        .select('*', { count: 'exact', head: true })
        .eq('followed_id', profileId);

      if (followersError) throw followersError;

      // Get following count
      const { count: followingCount, error: followingError } = await supabase
        .from('profile_follows')
        .select('*', { count: 'exact', head: true })
        .eq('follower_id', profileId);

      if (followingError) throw followingError;

      return {
        postsCount: postsCount || 0,
        followersCount: followersCount || 0,
        followingCount: followingCount || 0,
      } as ProfileStats;
    },
    enabled: !!profileId,
  });
}

export function useIsFollowing(targetProfileId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['is-following', user?.id, targetProfileId],
    queryFn: async () => {
      if (!user?.id || !targetProfileId) return false;

      const { data, error } = await supabase
        .from('profile_follows')
        .select('id')
        .eq('follower_id', user.id)
        .eq('followed_id', targetProfileId)
        .maybeSingle();

      if (error) throw error;
      return !!data;
    },
    enabled: !!user?.id && !!targetProfileId && user.id !== targetProfileId,
  });
}
